import React, { useCallback, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { AuthSelectors } from "src/redux/Auth/selectors";
import { useDispatch, useSelector } from "react-redux";
import { clientPoolPositionsColumnDefs, defaultColDef } from "./column_defs";
import DetailCellRenderer from "./DetailCellRenderer";
import styles from "./styles.module.scss";
import "src/styles/agGridCustom.scss";
import fetchWrapper from "src/utils/fetchWrapper";
import { TableActions } from "src/redux/Table/actions";
import { ClientPoolSelectors } from "src/redux/ClientPool/selectors";
import { ClientPoolActions } from "src/redux/ClientPool/actions";

const getLastRowIndex = (request, response) => {
    if (!response) return undefined;
    return response.lastRow < request.endRow ? response.lastRow : undefined;
};

const Positions = () => {
    const dispatch = useDispatch();
    const gridRef = React.useRef();
    const securityListPositionsUrl = useSelector(
        AuthSelectors.BuildSecurityListPositionsUrl
    );
    const haveUpdates = useSelector(ClientPoolSelectors.HaveUpdates);

    const handleColumnStateChange = (params) =>
        dispatch(
            TableActions.SetColumnState(params.columnApi.getColumnState())
        );

    const createServerSideDatasource = useCallback(() => {
        return {
            getRows: (params) => {
                const body = JSON.stringify(params.request);

                dispatch(
                    fetchWrapper(securityListPositionsUrl, {
                        method: "POST",
                        body,
                        headers: {
                            Accept: "application/json",
                            "Content-Type": "application/json",
                        },
                    })
                )
                    .then((response) => {
                        const data = response.data || [];
                        // Mark rows with num_owners > 0 as master rows
                        const processedData = data.map((row) => ({
                            ...row,
                            __hasDetailPanel: row.num_owners > 0,
                        }));
                        params.successCallback(
                            processedData,
                            getLastRowIndex(params.request, response)
                        );
                    })
                    .catch((error) => {
                        console.error("Error fetching positions data:", error);
                        params.failCallback();
                    });
            },
        };
    }, [dispatch, securityListPositionsUrl]);

    const onGridReady = useCallback(
        (params) => {
            params.api.sizeColumnsToFit();
            const datasource = createServerSideDatasource();
            params.api.setServerSideDatasource(datasource);
        },
        [createServerSideDatasource]
    );

    /**
     *  Refetches data when there's update
     */
    useEffect(() => {
        if (!gridRef.current?.api) return;
        if (haveUpdates) {
            gridRef.current.api.refreshServerSideStore();
            dispatch(ClientPoolActions.SetHaveUpdates(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [haveUpdates]);

    return (
        <div className={`${styles.table} ag-theme-alpine-dark`}>
            <AgGridReact
                ref={gridRef}
                defaultColDef={defaultColDef}
                columnDefs={clientPoolPositionsColumnDefs}
                headerHeight={40}
                groupHeaderHeight={40}
                rowHeight={40}
                rowModelType="serverSide"
                serverSideStoreType={"partial"}
                onGridReady={onGridReady}
                rowBuffer={100}
                cacheBlockSize={50}
                blockLoadDebounceMillis={500}
                animateRows={true}
                masterDetail={true}
                isRowMaster={(dataItem) => dataItem.__hasDetailPanel}
                detailCellRenderer={DetailCellRenderer}
                detailRowAutoHeight={true}
                suppressRowClickSelection={true}
                onColumnResized={handleColumnStateChange}
                onColumnPinned={handleColumnStateChange}
                onColumnMoved={handleColumnStateChange}
                onColumnVisible={handleColumnStateChange}
                onSortChanged={handleColumnStateChange}
                embedFullWidthRows={true} // sync horizontal scrolling between master and detail tables
            />
        </div>
    );
};
export default Positions;
